import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EnvelopeSimple,
  File,
  Files,
  MagnifyingGlass,
  PaperPlaneTilt,
  Paperclip,
  UploadSimple,
  UserPlus,
  X,
} from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  documents as documentsApi,
  mail as mailApi,
} from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";


function FieldLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1.5 block text-xs font-black uppercase tracking-wide text-[var(--do-muted)]"
    >
      {children}
    </label>
  );
}


export function RecipientField({
  field,
  placeholder,
  required = false,
  draft,
  setDraft,
  onCheckRecipient,
  onSearchRecipients,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const requestRef = useRef(0);
  const value = draft[field] || "";
  const search = value.split(/[;,]/).pop().trim();

  useEffect(() => {
    if (!onSearchRecipients || search.length < 2) {
      setItems([]);
      setOpen(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      try {
        const response = await onSearchRecipients(search);
        if (requestId !== requestRef.current) return;
        setItems(response?.items || []);
        setOpen(true);
      } catch {
        if (requestId !== requestRef.current) return;
        setItems([]);
        setOpen(false);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [onSearchRecipients, search]);

  function choose(contact) {
    const existing = value
      .split(/[;,]/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    const prefix = search ? existing.slice(0, -1) : existing;
    const duplicate = prefix.some(
      (entry) => entry.toLowerCase() === contact.email.toLowerCase()
    );
    const next = duplicate ? prefix : [...prefix, contact.email];

    setDraft((current) => ({
      ...current,
      [field]: next.join(", "),
      ...(field === "to" ? { prospectId: contact.id || "" } : {}),
    }));
    setItems([]);
    setOpen(false);
  }

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={(event) => setDraft((current) => ({
          ...current,
          [field]: event.target.value,
          ...(field === "to" ? { prospectId: "" } : {}),
        }))}
        onFocus={() => items.length && setOpen(true)}
        onBlur={() => {
          window.setTimeout(() => setOpen(false), 150);
          if (field === "to") onCheckRecipient();
        }}
        placeholder={placeholder}
        className="min-h-[44px]"
        required={required}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={open}
        aria-controls={`mail-${field}-suggestions`}
      />
      {open && (
        <div
          id={`mail-${field}-suggestions`}
          role="listbox"
          className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-1 shadow-xl"
        >
          {items.length ? items.map((contact) => (
            <button
              key={contact.id}
              type="button"
              role="option"
              aria-selected="false"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(contact)}
              className="flex min-h-[44px] w-full flex-col rounded-lg px-3 py-2 text-left hover:bg-[var(--do-bg-2)] focus:bg-[var(--do-bg-2)]"
            >
              <span className="text-sm font-bold text-[var(--do-ink)]">
                {contact.name || contact.email}
              </span>
              <span className="text-xs text-[var(--do-muted)]">
                {[contact.company, contact.email].filter(Boolean).join(" · ")}
              </span>
            </button>
          )) : (
            <div className="px-3 py-2 text-xs text-[var(--do-muted)]">
              Aucun contact trouvé — vous pouvez saisir l’adresse manuellement.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function MailContactAssociationDialog({
  open,
  suggestedEmail = "",
  saving = false,
  onClose,
  onSearchRecipients,
  onAssociate,
}) {
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setItems([]);
      setSelectedContact(null);
      setLoading(false);
      return;
    }

    setSearch(String(suggestedEmail || ""));
    setItems([]);
    setSelectedContact(null);
  }, [open, suggestedEmail]);

  useEffect(() => {
    const term = search.trim();

    if (
      !open
      || !onSearchRecipients
      || term.length < 2
      || (
        selectedContact?.email
        && term.toLowerCase() === selectedContact.email.toLowerCase()
      )
    ) {
      setItems([]);
      setLoading(false);
      return undefined;
    }

    const requestId = ++requestRef.current;
    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const response = await onSearchRecipients(term);

        if (requestId !== requestRef.current) {
          return;
        }

        setItems(response?.items || []);
      } catch {
        if (requestId === requestRef.current) {
          setItems([]);
        }
      } finally {
        if (requestId === requestRef.current) {
          setLoading(false);
        }
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [
    open,
    onSearchRecipients,
    search,
    selectedContact,
  ]);

  if (!open) return null;

  function choose(contact) {
    setSelectedContact(contact);
    setSearch(contact?.email || "");
    setItems([]);
  }

  return (
    <ActaModal
      open
      onClose={saving ? undefined : onClose}
      title="Associer à un contact"
    >
      <div
        className="min-w-0 space-y-4 [overflow-wrap:anywhere]"
        data-testid="mail-contact-association-dialog"
      >
        <div>
          <FieldLabel htmlFor="mail-contact-association-search">
            Contact
          </FieldLabel>
          <div className="relative">
            <MagnifyingGlass
              size={17}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
            />
            <Input
              id="mail-contact-association-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setSelectedContact(null);
              }}
              placeholder="Nom, société ou e-mail"
              className="min-h-[44px] pl-10"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
          {loading ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Recherche…
            </div>
          ) : items.length > 0 ? (
            items.map((contact) => (
              <button
                key={contact.id}
                type="button"
                onClick={() => choose(contact)}
                className="flex min-h-[56px] w-full flex-col border-b border-[var(--do-border)] px-4 py-3 text-left last:border-b-0 hover:bg-[var(--do-bg-2)]"
              >
                <span className="text-sm font-black text-[var(--do-ink)]">
                  {contact.name || contact.email}
                </span>
                <span className="text-xs text-[var(--do-muted)]">
                  {[contact.company, contact.email]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </button>
            ))
          ) : selectedContact ? (
            <div className="p-4">
              <div className="text-sm font-black text-[var(--do-ink)]">
                {selectedContact.name || selectedContact.email}
              </div>
              <div className="mt-1 text-xs text-[var(--do-muted)]">
                {[selectedContact.company, selectedContact.email]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
          ) : search.trim().length >= 2 ? (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Aucun contact accessible trouvé.
            </div>
          ) : (
            <div className="p-4 text-sm text-[var(--do-muted)]">
              Saisissez au moins deux caractères.
            </div>
          )}
        </div>

        <p className="text-xs text-[var(--do-muted)]">
          Seuls les contacts Commercial autorisés pour votre compte sont proposés.
        </p>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="actaSecondary"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="actaPrimary"
            onClick={() => onAssociate(selectedContact)}
            disabled={!selectedContact || saving}
          >
            {saving ? "Association…" : "Associer"}
          </Button>
        </div>
      </div>
    </ActaModal>
  );
}


export function DocumentAttachmentPicker({
  open,
  onClose,
  onAttach,
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setLoading(true);

    documentsApi
      .list()
      .then((items) => {
        if (!cancelled) {
          setDocuments(Array.isArray(items) ? items : []);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(
            error?.response?.data?.detail
            || "Impossible de charger les Documents"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch("");
      setSelectedIds([]);
    }
  }, [open]);

  const visibleDocuments = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase("fr-FR");
    if (!needle) return documents;

    return documents.filter((document) =>
      String(document?.name || "")
        .toLocaleLowerCase("fr-FR")
        .includes(needle)
    );
  }, [documents, search]);

  function toggleDocument(documentId) {
    setSelectedIds((current) =>
      current.includes(documentId)
        ? current.filter((id) => id !== documentId)
        : [...current, documentId]
    );
  }

  async function attachSelected() {
    if (!selectedIds.length) return;

    setAttaching(true);

    try {
      const selectedDocuments = documents.filter((document) =>
        selectedIds.includes(document.id)
      );

      const files = await Promise.all(
        selectedDocuments.map(async (document) => {
          const blob = await documentsApi.getFile(document.id);
          return new window.File(
            [blob],
            document.name || "document",
            {
              type:
                blob?.type
                || document.mimeType
                || "application/octet-stream",
            }
          );
        })
      );

      onAttach(files);
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Impossible d'ajouter le document au mail"
      );
    } finally {
      setAttaching(false);
    }
  }

  if (!open) return null;

  return (
    <ActaModal
      open
      onClose={attaching ? undefined : onClose}
      title="Ajouter depuis Documents"
    >
      <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]" data-testid="mail-document-picker">
        <div className="relative">
          <MagnifyingGlass
            size={17}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--do-muted)]"
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un document"
            className="min-h-[44px] pl-10"
            autoFocus
          />
        </div>

        <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
          {loading ? (
            <div className="p-5 text-sm text-[var(--do-muted)]">
              Chargement des Documents…
            </div>
          ) : visibleDocuments.length === 0 ? (
            <div className="p-5 text-sm text-[var(--do-muted)]">
              Aucun document accessible.
            </div>
          ) : (
            visibleDocuments.map((document) => {
              const selected = selectedIds.includes(document.id);

              return (
                <button
                  key={document.id}
                  type="button"
                  onClick={() => toggleDocument(document.id)}
                  className={[
                    "flex min-h-[56px] w-full items-center gap-3 border-b border-[var(--do-border)] px-4 py-3 text-left last:border-b-0",
                    selected ? "bg-[var(--do-bg-2)]" : "hover:bg-[var(--do-bg-2)]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-black",
                      selected
                        ? "border-[var(--do-accent)] bg-[var(--do-accent)] text-white"
                        : "border-[var(--do-border)]",
                    ].join(" ")}
                    aria-hidden="true"
                  >
                    {selected ? "✓" : ""}
                  </span>
                  <File size={20} className="shrink-0 text-[var(--do-muted)]" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-[var(--do-ink)]">
                      {document.name || "Document"}
                    </span>
                    <span className="block text-xs text-[var(--do-muted)]">
                      {document.extension
                        ? document.extension.toUpperCase()
                        : "Fichier"}
                      {Number.isFinite(Number(document.size))
                        ? ` · ${Math.max(1, Math.round(Number(document.size) / 1024))} Ko`
                        : ""}
                    </span>
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="actaSecondary"
            onClick={onClose}
            disabled={attaching}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="actaPrimary"
            onClick={attachSelected}
            disabled={!selectedIds.length || attaching}
          >
            {attaching
              ? "Ajout…"
              : `Ajouter${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
          </Button>
        </div>
      </div>
    </ActaModal>
  );
}


export function MailComposerForm({
  draft,
  setDraft,
  sending,
  onClose,
  onSubmit,
  onSchedule,
  onCheckRecipient,
  onSearchRecipients,
}) {
  const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
  const [documentPickerOpen, setDocumentPickerOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [, setSearchParams] = useSearchParams();
  const localFileInputRef = useRef(null);

  function appendFiles(files) {
    const nextFiles = Array.from(files || []);
    if (!nextFiles.length) return;

    setDraft((current) => ({
      ...current,
      files: [
        ...(current.files || []),
        ...nextFiles,
      ],
    }));
  }

  function removeFile(index) {
    setDraft((current) => ({
      ...current,
      files: (current.files || []).filter((_, fileIndex) => fileIndex !== index),
    }));
  }

  async function saveCurrentDraft() {
    const hasContent = [
      draft.to,
      draft.cc,
      draft.bcc,
      draft.subject,
      draft.body,
      draft.prospectId,
      draft.inReplyTo,
      draft.references,
    ].some((value) => String(value || "").trim());

    if (!hasContent) {
      toast.error("Le brouillon est vide");
      return;
    }

    setSavingDraft(true);

    try {
      const payload = {
        to: draft.to || "",
        cc: draft.cc || "",
        bcc: draft.bcc || "",
        subject: draft.subject || "",
        body: draft.body || "",
        prospect_id: draft.prospectId || "",
        in_reply_to: draft.inReplyTo || "",
        references: draft.references || "",
      };

      if (draft.id) {
        await mailApi.updateDraft(
          draft.id,
          payload
        );
      } else {
        await mailApi.saveDraft(
          payload
        );
      }

      toast.success(
        draft.files?.length
          ? "Brouillon enregistré sans les pièces jointes"
          : draft.id
            ? "Brouillon modifié"
            : "Brouillon enregistré"
      );

      setSearchParams({
        folder: "drafts",
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Enregistrement du brouillon impossible"
      );
    } finally {
      setSavingDraft(false);
    }
  }

  return (
    <>
      <form
        onSubmit={onSubmit}
        className="flex min-h-0 min-w-0 w-full flex-1 flex-col lg:min-h-full"
        data-testid="mail-compose-form"
      >
        <div className="flex flex-col gap-3 border-b border-[var(--do-border)] p-4 sm:flex-row sm:items-center sm:justify-between lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)]">
              <EnvelopeSimple size={19} weight="duotone" />
            </div>
            <div className="min-w-0">
              <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
                {draft.id ? "Modifier le brouillon" : "Nouveau mail"}
              </h2>
              <p className="text-xs text-[var(--do-muted)]">
                {draft.id
                  ? "Reprendre et modifier le brouillon"
                  : "Rédiger et envoyer un message"}
              </p>
            </div>
          </div>
          <Button type="button" variant="actaSecondary" className="w-full sm:w-auto" onClick={onClose}>Annuler</Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3 p-4 lg:p-6">
          <RecipientField field="to" placeholder="À" required draft={draft} setDraft={setDraft} onCheckRecipient={onCheckRecipient} onSearchRecipients={onSearchRecipients} />
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <RecipientField field="cc" placeholder="Cc" draft={draft} setDraft={setDraft} onCheckRecipient={onCheckRecipient} onSearchRecipients={onSearchRecipients} />
            <RecipientField field="bcc" placeholder="Cci" draft={draft} setDraft={setDraft} onCheckRecipient={onCheckRecipient} onSearchRecipients={onSearchRecipients} />
          </div>
          <Input value={draft.subject} onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))} placeholder="Objet" className="min-h-[44px]" />
          <Textarea value={draft.body} onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))} placeholder="Écrivez votre message…" className="min-h-[300px] flex-1 resize-none sm:min-h-[360px] lg:min-h-[420px]" />

          {(draft.files || []).length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="mail-attachment-list">
              {(draft.files || []).map((file, index) => (
                <span
                  key={`${file.name}-${file.size}-${index}`}
                  className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-2 text-xs font-bold text-[var(--do-ink)]"
                >
                  <Paperclip size={14} className="shrink-0" />
                  <span className="max-w-[240px] truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-[var(--do-bg-solid)]"
                    aria-label={`Retirer ${file.name}`}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="flex flex-col gap-3 border-t border-[var(--do-border)] pt-4 sm:flex-row sm:items-end sm:justify-between">
            <div className="relative">
              <button
                type="button"
                onClick={() => setAttachmentMenuOpen((current) => !current)}
                className="inline-flex min-h-[42px] items-center gap-2 rounded-xl border border-[var(--do-border)] px-3 py-2 text-sm font-bold text-[var(--do-ink)] transition hover:bg-[var(--do-bg-2)]"
                aria-expanded={attachmentMenuOpen}
                data-testid="mail-attachment-button"
              >
                <Paperclip size={17} />
                <span>
                  {draft.files?.length
                    ? `${draft.files.length} pièce${draft.files.length > 1 ? "s" : ""} jointe${draft.files.length > 1 ? "s" : ""}`
                    : "Ajouter une pièce jointe"}
                </span>
              </button>

              {attachmentMenuOpen && (
                <div className="absolute bottom-full left-0 z-20 mb-2 min-w-[230px] overflow-hidden rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentMenuOpen(false);
                      localFileInputRef.current?.click();
                    }}
                    className="flex min-h-[48px] w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-[var(--do-ink)] hover:bg-[var(--do-bg-2)]"
                  >
                    <UploadSimple size={19} />
                    Depuis l’appareil
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAttachmentMenuOpen(false);
                      setDocumentPickerOpen(true);
                    }}
                    className="flex min-h-[48px] w-full items-center gap-3 border-t border-[var(--do-border)] px-4 py-3 text-left text-sm font-bold text-[var(--do-ink)] hover:bg-[var(--do-bg-2)]"
                    data-testid="mail-attachment-from-documents"
                  >
                    <Files size={19} />
                    Depuis Documents
                  </button>
                </div>
              )}

              <input
                ref={localFileInputRef}
                type="file"
                multiple
                className="sr-only"
                onChange={(event) => {
                  appendFiles(event.target.files);
                  event.target.value = "";
                }}
              />
            </div>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button
                type="button"
                variant="actaSecondary"
                className="w-full sm:w-auto"
                onClick={saveCurrentDraft}
                disabled={sending || savingDraft}
                data-testid="mail-save-draft"
              >
                {savingDraft
                  ? "Enregistrement…"
                  : draft.id
                    ? "Enregistrer les modifications"
                    : "Enregistrer le brouillon"}
              </Button>

              <Button
                type="button"
                variant="actaSecondary"
                disabled={sending || savingDraft}
                className="w-full sm:w-auto"
                aria-expanded={scheduleOpen}
                data-testid="mail-schedule-toggle"
                onClick={() => setScheduleOpen((current) => !current)}
              >
                Planifier l’envoi
              </Button>

              <Button
                type="submit"
                variant="actaPrimary"
                disabled={sending || savingDraft}
                className="w-full gap-2 sm:w-auto"
              >
                <PaperPlaneTilt size={17} weight="duotone" />
                {sending ? "Envoi…" : "Envoyer"}
              </Button>
            </div>
          </div>

          {scheduleOpen && (
            <div
              className="flex flex-wrap items-end gap-3 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-3"
              data-testid="mail-schedule-panel"
            >
              <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-sm font-bold text-[var(--do-ink)]">
                Date et heure de l’envoi
                <input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(event) => setScheduledAt(event.target.value)}
                  className="min-h-[44px] rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 text-[var(--do-ink)]"
                  data-testid="mail-scheduled-at"
                />
              </label>
              <Button
                type="button"
                variant="actaPrimary"
                disabled={sending || savingDraft || !scheduledAt}
                data-testid="mail-confirm-schedule"
                onClick={() => onSchedule(scheduledAt)}
              >
                {sending ? "Planification…" : "Confirmer la planification"}
              </Button>
              <p className="w-full text-xs text-[var(--do-muted)]">
                L’heure sélectionnée est celle de votre appareil. L’envoi sera traité côté serveur.
              </p>
            </div>
          )}
        </div>
      </form>

      <DocumentAttachmentPicker
        open={documentPickerOpen}
        onClose={() => setDocumentPickerOpen(false)}
        onAttach={appendFiles}
      />
    </>
  );
}


export function MailSetupDialog({ open, setup, setSetup, saving, onClose, onSubmit }) {
  const mobileUnsupported = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  if (!open || mobileUnsupported) return null;

  return (
    <ActaModal open onClose={onClose} title="Connexion de la boîte mail">
      <form onSubmit={onSubmit} className="space-y-5" data-testid="mail-setup-form">
        <section className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4">
          <h3 className="font-heading text-sm font-black text-[var(--do-ink)]">Compte configuré par votre administrateur</h3>
          <p className="mt-1 text-xs text-[var(--do-muted)]">Les paramètres de réception et d’envoi sont gérés par l’administrateur. Vous ne renseignez ici que votre mot de passe lorsque l’accès vous a été ouvert.</p>
          <div className="mt-4 space-y-3">
            <div><FieldLabel htmlFor="mail-setup-email">Adresse e-mail</FieldLabel><Input id="mail-setup-email" type="email" value={setup.email} readOnly disabled className="min-h-[44px]" /></div>
            <div><FieldLabel htmlFor="mail-setup-password">Mot de passe</FieldLabel><Input id="mail-setup-password" type="password" required value={setup.password} onChange={(event) => setSetup((current) => ({ ...current, password: event.target.value }))} placeholder="Mot de passe de la boîte mail" className="min-h-[44px]" autoComplete="new-password" /></div>
          </div>
        </section>
        <section className="rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"><div className="grid gap-3 sm:grid-cols-2"><div><div className="label-eyebrow">Réception IMAP</div><div className="mt-1 text-sm text-[var(--do-ink)]">{setup.imap_host}:{setup.imap_port}</div></div><div><div className="label-eyebrow">Envoi SMTP</div><div className="mt-1 text-sm text-[var(--do-ink)]">{setup.smtp_host}:{setup.smtp_port}</div></div></div></section>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end"><Button type="button" variant="actaSecondary" onClick={onClose}>Annuler</Button><Button type="submit" variant="actaPrimary" disabled={saving || !setup.password.trim()}>{saving ? "Validation…" : "Enregistrer et tester"}</Button></div>
      </form>
    </ActaModal>
  );
}


export function UnknownMailContactDialog({ contact, onClose, onNeverAsk, onCreate }) {
  if (!contact) return null;
  return (
    <ActaModal open onClose={onClose} title="Contact inconnu">
      <div className="min-w-0 space-y-4 [overflow-wrap:anywhere]"><div className="flex items-start gap-3 rounded-xl border border-[var(--do-border)] p-4"><UserPlus size={22} className="mt-0.5 text-[var(--do-accent)]" /><div><div className="font-black text-[var(--do-ink)]">{contact.email}</div><p className="mt-1 text-sm text-[var(--do-muted)]">Cette adresse n'existe pas dans les contacts Acta. Voulez-vous créer le contact ?</p></div></div><div className="flex flex-col gap-2 sm:flex-row sm:justify-end"><Button type="button" variant="actaSecondary" onClick={onClose}>Plus tard</Button><Button type="button" variant="actaGhost" onClick={onNeverAsk}>Ne plus demander</Button><Button type="button" variant="actaPrimary" onClick={onCreate}>Créer le contact</Button></div></div>
    </ActaModal>
  );
}
